import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CustomCart } from '@cart/const';
import { CartActions, CartApiService, CloneCartFacade, CustomActiveCartService } from '@cart/data-access';
import { CityService } from '@city/feature';
import { EnvironmentService } from '@core/feature';
import { Actions, EffectNotification, createEffect, ofType } from '@ngrx/effects';
import { Cart, GlobalMessageActions, GlobalMessageType } from '@spartacus/core';
import { EMPTY, Observable, combineLatest, from, iif } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { CustomMultiCartService } from '../services';

@Injectable()
export class CartCloneEffects {
  cloneCart$: Observable<CartActions.CloneCartSuccess | CartActions.CloneCartFail | GlobalMessageActions.AddMessage> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CartActions.CLONE_CART),
        map((action: CartActions.CloneCart) => action.payload),
        mergeMap(({ cartGuid }) => {
          return this.occCartService.cloneCart(cartGuid).pipe(
            map((cart: Cart) => {
              return new CartActions.CloneCartSuccess(cart);
            }),
            catchError((error: any) =>
              from([
                new CartActions.CloneCartFail(error),
                new GlobalMessageActions.AddMessage({
                  text: { raw: 'Ошибка получения корзины' },
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                }),
              ]),
            ),
          );
        }),
      ),
    );
  //  если корзинки нет совсем, ее уже можно ставить
  // если она есть, ставить ее нельзя, из-за лишних запросов которые ломают корзину,
  // придется ждать интерсептор
  cloneCartSuccess$: Observable<GlobalMessageActions.AddMessage> = createEffect(() =>
    this.actions$.pipe(
      ofType(CartActions.CLONE_CART_SUCCESS),
      switchMap(() =>
        this.customActiveCartService.getActiveCartId().pipe(
          take(1),
          tap((cartId: string) => {
            if (!cartId) {
              this.customActiveCartService.setActiveClonnedCart();
            }
          }),
        ),
      ),
      map(() => {
        const messageAction = new GlobalMessageActions.AddMessage({
          text: {
            raw: 'Вы перешли по ссылке-приглашению в корзину, которой с вами поделились. Теперь вы можете управлять этой корзиной',
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        });

        return messageAction;
      }),

      tap(() => this.router.navigate([], { queryParams: { cloneFrom: undefined }, queryParamsHandling: 'merge' })),
    ),
  );

  // следит, чтобы при изменении активной корзины проставлялся ее бэйсстор
  // собирает в один список корзинки и зашериную корзинку, данные которой еще
  // не попали в общий список
  changeCityAfterCloneCart$: Observable<never> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.SET_ACTIVE_CART_ID),
        map((action: CartActions.SetActiveCartId) => action.payload),
        switchMap((cartId: string) =>
          combineLatest([
            this.customMultiCartService.getCartById(cartId),
            this.cloneCartFacade.getClonedCartValue(),
          ]).pipe(
            filter(([, clonedCart]) => Boolean(clonedCart)),
            take(1),
            map((carts: CustomCart[]) => {
              return carts.filter(Boolean).find((cart) => {
                return cart.code === cartId || cart.guid === cartId;
              });
            }),
            filter<CustomCart>(Boolean),
            withLatestFrom(this.cityService.activeBaseStoreCode$),
            map(([cart, code]: [CustomCart, string]) => {
              if (code !== cart.baseStoreId) {
                this.cityService.setBaseStoreWithoutCompareCart(cart.baseStoreId);
              }
            }),
          ),
        ),
        switchMap(() => EMPTY),
      ),
    { dispatch: false },
  );

  // Аноним у нас вполне себе может иметь несколько анонимных корзин в сторадже.
  // Они получаются при поделиться корзиной и возможно при истекшей авторизации(неточно)
  // после авторизации происходит мерж активной корзинки и переезд ее в пользовательскую.
  // гуид меняется на ид
  // но
  // остальные анонимные корзины в сторе начинают отображаться в интерфейсе,
  // но переключиться на них нельзя, потому что они анонимные и на запрос данных по ним
  // мы получаем карт нот фаунд
  //
  // Решил удалаять все анонимные корзины после мержа, которые не наша померженная.

  deleteOldAnonymousCartAfterLoginMerge: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.MERGE_CART_SUCCESS),
        withLatestFrom(this.customMultiCartService.getCarts()),
        map(([action, carts]: [CartActions.MergeCartSuccess, CustomCart[]]) => {
          carts.map((cart: CustomCart) => {
            if (cart.code !== action.payload.cartId) {
              this.customMultiCartService.removeCart(cart.guid);
            }
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private occCartService: CartApiService,
    private router: Router,
    private customActiveCartService: CustomActiveCartService,
    private customMultiCartService: CustomMultiCartService,
    private cloneCartFacade: CloneCartFacade,
    private cityService: CityService,
    private environmentService: EnvironmentService,
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return iif(() => this.environmentService.isPlatformBrowser, resolvedEffects$, EMPTY);
  }
}
